import { z } from 'zod'
import { DisplayName, Password, UserPublic, Username } from './user.js'

// Auth wire payloads.
//
// Field names stay snake_case (iOS Swift CodingKeys compatibility). The
// access token is returned in the JSON body; the refresh token rides a
// HttpOnly refresh cookie set by the auth routes and never
// surfaces to JS.

export const AuthRegisterRequest = z.object({
  username: Username,
  password: Password,
  display_name: DisplayName.optional(),
})
export type AuthRegisterRequest = z.infer<typeof AuthRegisterRequest>

export const AuthLoginRequest = z.object({
  username: Username,
  password: Password,
})
export type AuthLoginRequest = z.infer<typeof AuthLoginRequest>

export const AuthAppleExchangeRequest = z.object({
  id_token: z.string().min(1),
  nonce: z.string().min(1).max(256),
  display_name: DisplayName.optional(),
  // One-time Apple authorization code (ASAuthorizationAppleIDCredential.authorizationCode).
  // Optional for back-compat with older clients. When present, the backend exchanges it
  // with Apple for a refresh_token (stored encrypted) so account deletion can revoke it.
  authorization_code: z.string().min(1).max(2048).optional(),
})
export type AuthAppleExchangeRequest = z.infer<typeof AuthAppleExchangeRequest>

export const AuthResponse = z.object({
  access_token: z.string(),
  token_type: z.literal('Bearer'),
  expires_in: z.number().int().positive(),
  user: UserPublic,
})
export type AuthResponse = z.infer<typeof AuthResponse>

export const AuthQrChallengeCreateResponse = z.object({
  challenge_id: z.string().uuid(),
  qr_payload: z.string().min(1),
  expires_at: z.string().datetime({ offset: true }),
  poll_after_ms: z.number().int().positive(),
})
export type AuthQrChallengeCreateResponse = z.infer<typeof AuthQrChallengeCreateResponse>

export const AuthQrChallengeStatus = z.enum(['pending', 'confirmed'])
export type AuthQrChallengeStatus = z.infer<typeof AuthQrChallengeStatus>

export const AuthQrChallengePollRequest = z.object({
  secret: z.string().min(32).max(256),
})
export type AuthQrChallengePollRequest = z.infer<typeof AuthQrChallengePollRequest>

export const AuthQrChallengePollResponse = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('pending'),
    expires_at: z.string().datetime({ offset: true }),
    poll_after_ms: z.number().int().positive(),
  }),
  z.object({
    status: z.literal('confirmed'),
    auth: AuthResponse,
  }),
])
export type AuthQrChallengePollResponse = z.infer<typeof AuthQrChallengePollResponse>

export const AuthQrChallengeConfirmRequest = z.object({
  secret: z.string().min(32).max(256),
})
export type AuthQrChallengeConfirmRequest = z.infer<typeof AuthQrChallengeConfirmRequest>

export const ErrorResponse = z.object({
  error: z.string(),
  message: z.string(),
})
export type ErrorResponse = z.infer<typeof ErrorResponse>
